import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill, KpiCard } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, dmyTime, num } from "@/lib/format";
import Medicines from "./Medicines";
import Suppliers from "./Suppliers";
import Purchases from "./Purchases";

const SECTIONS = [["medicines", "Medicines"], ["stock", "Stock"], ["suppliers", "Suppliers"],
  ["movements", "Stock movements"], ["purchases", "Purchase history"]];

function StockPanel() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "";
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [adjust, setAdjust] = useState(null);
  const [aForm, setAForm] = useState({ quantity: "", reason: "" });
  const [archiveFor, setArchiveFor] = useState(null); // { batch_id, medicine_id, batch_no }
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveReasonErr, setArchiveReasonErr] = useState("");
  const [showGroup, setShowGroup] = useState(false);
  // B.7: server-side pagination consumer. Page resets on filter/group change.
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    setItems(null);
    try {
      const [inv, sum] = await Promise.all([
        api.get("/pharmacy/inventory", { params: { filter: filter || undefined,
                                                 group: showGroup ? "medicine" : undefined,
                                                 page, limit: PAGE_SIZE } }),
        api.get("/pharmacy/stock/summary"),
      ]);
      setItems(inv.data.items);
      setTotal(inv.data.total ?? inv.data.items.length);
      setSummary(sum.data);
    } catch (e) { setError(apiError(e)); }
  }, [filter, showGroup, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("filter", v); else next.delete("filter");
    next.set("tab", "stock");
    setParams(next);
    setPage(1);
  };

  const toggleGroup = () => { setShowGroup((g) => !g); setPage(1); };
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const doAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post("/pharmacy/stock/adjust", { batch_id: adjust.batch_id,
        quantity: Number(aForm.quantity), reason: aForm.reason });
      toast.success("Stock adjusted and the movement was recorded.");
      setAdjust(null); setAForm({ quantity: "", reason: "" }); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doArchive = async (e) => {
    e.preventDefault();
    if (!archiveReason.trim() || archiveReason.trim().length < 5) {
      setArchiveReasonErr("Give a reason (at least 5 characters).");
      return;
    }
    try {
      await api.post(`/pharmacy/medicines/${archiveFor.medicine_id}/archive-batches`, {
        batch_ids: [archiveFor.batch_id],
        reason: archiveReason.trim(),
      });
      toast.success(`Batch ${archiveFor.batch_no} archived.`);
      setArchiveFor(null); setArchiveReason(""); setArchiveReasonErr(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const tabs = [["", "All batches"], ["low_stock", "Low stock"], ["expiring", "Expiring soon"],
    ["expired", "Expired"]];

  return (
    <div data-testid="stock-panel">
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {tabs.map(([v, label]) => (
          <button key={label} onClick={() => setFilter(v)} data-testid={`inventory-tab-${v || "all"}`}
            className={`rounded-full border px-5 py-2 text-sm font-medium ${filter === v
              ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>
            {label}
          </button>
        ))}
        <button onClick={toggleGroup} data-testid="inventory-group-toggle"
          className={`rounded-full border px-5 py-2 text-sm font-medium ${showGroup
            ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>
          {showGroup ? "Ungroup" : "Group by medicine"}
        </button>
        {pages > 1 && (
          <span className="ml-auto flex items-center gap-2 text-sm text-slate-600" data-testid="inventory-pager">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={ghostBtnCls} data-testid="inventory-prev">‹ Prev</button>
            <span data-testid="inventory-page-info">Page {page} of {pages} · {total} rows</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
              className={ghostBtnCls} data-testid="inventory-next">Next ›</button>
          </span>
        )}
      </div>
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard label="Opening (period)" value={num(summary?.opening ?? 0)} testid="kpi-opening" />
            <KpiCard label="Closing stock" value={num(summary?.closing ?? 0)} testid="kpi-closing" />
            <KpiCard label="Units in view" value={num(items.reduce((s, b) => s + b.quantity, 0))} testid="kpi-units" />
            <KpiCard label="Low stock items" value={num(summary?.low_stock ?? 0)} testid="kpi-low-stock" />
            <KpiCard label="Stock value (purchase)" tone="accent"
              value={inr(items.reduce((s, b) => s + b.quantity * b.purchase_price, 0))} testid="kpi-stock-value" />
          </div>
          {summary?.by_type && Object.keys(summary.by_type).length > 0 && (
            <div className="panel mb-8 p-5" data-testid="stock-movement-summary">
              <p className="overline mb-4 text-[#EA580C]">Movement by type</p>
              <div className="flex flex-wrap gap-6 text-sm">
                {Object.entries(summary.by_type).map(([k, v]) => (
                  <span key={k}>
                    <span className="block text-xs uppercase tracking-wider text-slate-400">{k.replace(/_/g, " ")}</span>
                    <span className={`font-semibold ${v < 0 ? "text-red-600" : "text-emerald-700"}`}>
                      {v > 0 ? `+${v}` : v}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {showGroup ? (
            <div data-testid="stock-grouped">
              {items.map((g) => (
                <div key={g.medicine_id} className="panel mb-5 p-4 border border-slate-200">
                  <div className="flex flex-wrap justify-between gap-3 items-baseline">
                    <div>
                      <p className="font-semibold text-slate-900">{g.medicine_name}</p>
                      <p className="text-xs text-slate-500">
                        batches {g.batch_count} · {g.packaging?.map(u => u.code).join(", ")}
                        · MRP {inr(g.mrp)} · SELL {inr(g.selling_price)} · GST {g.gst_percent}%
                        {g.batch_count > 1 ? ` · best before ${g.first_expiry}` : ""}
                      </p>
                    </div>
                    <div className="text-2xl font-bold text-emerald-700">{num(g.quantity)}</div>
                  </div>
                  {g.batch_count > 1 ? (
                    <Table testid={`grouped-batches-${g.medicine_id}`}
                      columns={["Batch", "Expiry", "Days left", "Quantity", "Purchase", "MRP", "Selling", "Supplier", "Status"]}
                      rows={g.batches}
                      empty={<EmptyState title="No subdivided batches" />}
                      renderRow={(b) => (
                        <tr key={b.batch_id}>
                          <Td className="font-mono-num text-slate-600">{b.batch_no}</Td>
                          <Td className="whitespace-nowrap text-slate-600">{dmy(b.expiry_date)}</Td>
                          <Td className={b.days_to_expiry < 0 ? "text-red-600" : b.expiry_flag === "expiring" ? "text-amber-700" : "text-slate-500"}>{b.days_to_expiry}</Td>
                          <Td className={b.low_stock ? "font-semibold text-amber-700" : "text-slate-900"}>{b.quantity}</Td>
                          <Td className="text-slate-600">{inr(b.purchase_price)}</Td>
                          <Td className="text-slate-600">{inr(b.mrp)}</Td>
                          <Td className="text-slate-900">{inr(b.selling_price)}</Td>
                          <Td className="text-slate-600">{b.supplier_name || "—"}</Td>
                          <Td><StatusPill status={b.expiry_flag === "safe" ? (b.low_stock ? "expiring" : "active") : b.expiry_flag} /></Td>
                        </tr>
                      )} />
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <Table testid="inventory-table"
              columns={["Medicine", "Batch", "Expiry", "Days left", "Quantity", "Purchase", "MRP", "Selling", "Supplier", "Status", ""]}
              rows={items} empty={<EmptyState title="No batches in this view" subtitle="Record a purchase to create batches." testid="inventory-empty" />}
              renderRow={(b) => (
                <tr key={b.batch_id} data-testid={`batch-row-${b.batch_id}`}>
                  <Td className="font-medium text-slate-900">{b.medicine_name}</Td>
                  <Td className="font-mono-num text-slate-600">{b.batch_no}</Td>
                  <Td className="whitespace-nowrap text-slate-600">{dmy(b.expiry_date)}</Td>
                  <Td className={b.days_to_expiry < 0 ? "text-red-600" : b.expiry_flag === "expiring" ? "text-amber-700" : "text-slate-500"}>{b.days_to_expiry}</Td>
                  <Td className={b.low_stock ? "font-semibold text-amber-700" : "text-slate-900"}>{b.quantity}</Td>
                  <Td className="text-slate-600">{inr(b.purchase_price)}</Td>
                  <Td className="text-slate-600">{inr(b.mrp)}</Td>
                  <Td className="text-slate-900">{inr(b.selling_price)}</Td>
                  <Td className="text-slate-600">{b.supplier_name || "—"}</Td>
                  <Td><StatusPill status={b.expiry_flag === "safe" ? (b.low_stock ? "expiring" : "active") : b.expiry_flag} /></Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-2">
                      {can("pharmacy.stock.adjust") && (
                        <button onClick={() => setAdjust(b)} className={ghostBtnCls} data-testid={`adjust-${b.batch_id}`}>Adjust</button>
                      )}
                      {(b.quantity || 0) === 0 && can("pharmacy.medicines.edit") && (
                        <button onClick={() => { setArchiveFor({ batch_id: b.batch_id, medicine_id: b.medicine_id, medicine_name: b.medicine_name, batch_no: b.batch_no }); setArchiveReason(""); setArchiveReasonErr(""); }}
                          className="text-xs font-medium text-red-600 hover:text-red-800" data-testid={`archive-batch-${b.batch_id}`}>
                          Archive
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              )} />
          )}
        </>
      )}
      <Modal open={Boolean(adjust)} onClose={() => setAdjust(null)} title="Stock adjustment"
        subtitle={adjust ? `${adjust.medicine_name} · batch ${adjust.batch_no} · current ${adjust.quantity}` : ""}
        testid="adjust-modal">
        <form onSubmit={doAdjust} className="grid gap-5 sm:grid-cols-2">
          <Field label="Change (+/-) *"><input required type="number" data-testid="adjust-qty-input" className={inputCls} value={aForm.quantity} onChange={(e) => setAForm({ ...aForm, quantity: e.target.value })} placeholder="e.g. -5" /></Field>
          <Field label="Reason *"><input required minLength={3} data-testid="adjust-reason-input" className={inputCls} value={aForm.reason} onChange={(e) => setAForm({ ...aForm, reason: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="adjust-submit-btn">Apply adjustment</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(archiveFor)} onClose={() => { setArchiveFor(null); setArchiveReason(""); setArchiveReasonErr(""); }}
        title="Archive empty batch"
        subtitle={archiveFor ? `${archiveFor.medicine_name || ""} · batch ${archiveFor.batch_no} · qty 0` : ""}
        testid="archive-batch-modal">
        <form onSubmit={doArchive} className="space-y-5">
          <p className="text-sm text-slate-600">
            Archiving removes this empty batch from stock views while preserving its purchase, sales and movement history. This cannot be undone.
          </p>
          {archiveReasonErr && <p className="text-sm text-red-600" data-testid="archive-reason-err">{archiveReasonErr}</p>}
          <Field label="Reason * (recorded in the audit log)">
            <input required minLength="5" className={inputCls} value={archiveReason}
              data-testid="archive-reason-input"
              placeholder="Why is this batch being archived?"
              onChange={(e) => setArchiveReason(e.target.value)} />
          </Field>
          <div className="sm:col-span-2">
            <button className={primaryBtnCls} data-testid="archive-submit-btn">Archive batch</button>
            <button type="button" onClick={() => { setArchiveFor(null); setArchiveReason(""); setArchiveReasonErr(""); }}
              className={ghostBtnCls} data-testid="archive-cancel-btn">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function MovementsPanel() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/pharmacy/stock/movements").then(({ data }) => setItems(data.items))
      .catch(() => setItems([]));
  }, []);
  if (!items) return <Loading />;
  return (
    <Table testid="movements-table"
      columns={["Date & time", "Medicine", "Batch", "Type", "Change", "Reference / reason", "User"]}
      rows={items} empty={<EmptyState title="No stock movements yet" subtitle="Purchases, sales, returns and adjustments appear here." testid="movements-empty" />}
      renderRow={(t) => (
        <tr key={t.movement_id} data-testid={`movement-row-${t.movement_id}`}>
          <Td className="whitespace-nowrap text-slate-500">{dmyTime(t.created_at)}</Td>
          <Td className="font-medium text-slate-900">{t.medicine_name || "—"}</Td>
          <Td className="font-mono-num text-slate-600">{t.batch_no || "—"}</Td>
          <Td className="capitalize text-slate-600">{(t.type || "").replace(/_/g, " ")}</Td>
          <Td className={t.change < 0 ? "font-semibold text-red-600" : "font-semibold text-emerald-700"}>
            {t.change > 0 ? `+${t.change}` : t.change}
          </Td>
          <Td className="text-slate-600">{t.reason || "—"}</Td>
          <Td className="text-slate-500">{t.by_name || t.by || "—"}</Td>
        </tr>
      )} />
  );
}

const ADMIN_ROLES = ["business_admin", "pharmacy_admin", "platform_owner"];

function InventoryDangerZone() {
  const { can, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Mirror of the server-side require_admin + permission gate: the button only
  // renders for admins holding pharmacy.data.reset; the API re-checks both.
  if (!ADMIN_ROLES.includes(user?.role) || !can("pharmacy.data.reset")) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (confirmation !== "DELETE INVENTORY") {
      toast.error("Type DELETE INVENTORY exactly to confirm the deletion.");
      return;
    }
    setBusy(true);
    try {
      await api.delete("/pharmacy/inventory",
        { data: { confirmation, reason: reason.trim() } });
      toast.success("Entire pharmacy inventory deleted. Medicines, batches, sales, purchases and stock movements were permanently removed.");
      setOpen(false);
      setConfirmation("");
      setReason("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel mt-10 border border-red-200 p-5" data-testid="inventory-danger-zone">
      <p className="overline text-red-600">Danger zone</p>
      <h3 className="mt-1 font-semibold text-slate-900">Delete entire inventory</h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-600">
        Permanently deletes every medicine, batch, sale, purchase and stock movement belonging to
        <em> this business only</em>. Suppliers, customers and the shared medicine catalogue are kept.
        This cannot be undone — use it when you want a completely fresh pharmacy start-over.
        (To keep the medicine catalogue and only clear stock, use Reset Pharmacy Data instead.)
      </p>
      <button onClick={() => setOpen(true)}
        className="mt-4 inline-flex items-center rounded-full bg-red-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-red-700"
        data-testid="delete-inventory-btn">
        Delete Entire Inventory
      </button>
      <Modal open={open} onClose={() => setOpen(false)}
        title="Delete entire inventory permanently"
        subtitle="All medicines, batches, sales, purchases and stock movements of this business will be deleted."
        testid="delete-inventory-modal">
        <form onSubmit={submit} className="space-y-5">
          <p className="text-sm text-slate-600">
            Unlike Reset Pharmacy Data, this also deletes the medicine catalogue of this business.
            Other businesses and the shared master medicine catalogue are not affected.
            This action cannot be undone.
          </p>
          <Field label="Reason * (recorded in the audit log)">
            <input required minLength={5} className={inputCls} value={reason}
              data-testid="delete-inventory-reason"
              placeholder="Why is the entire inventory being deleted?"
              onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Field label="Type DELETE INVENTORY to confirm *">
            <input required className={inputCls} value={confirmation}
              data-testid="delete-inventory-confirmation" placeholder="DELETE INVENTORY"
              onChange={(e) => setConfirmation(e.target.value)} />
          </Field>
          <button disabled={busy}
            className="inline-flex items-center rounded-full bg-red-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            data-testid="delete-inventory-submit">
            {busy ? "Deleting…" : "Delete Entire Inventory"}
          </button>
        </form>
      </Modal>
    </section>
  );
}

export default function Inventory() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "medicines";

  if (!can("pharmacy.inventory.view")) return <NoAccess what="inventory" />;

  const go = (value) => {
    const next = new URLSearchParams(params);
    next.set("tab", value);
    next.delete("filter");
    setParams(next);
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader overline="Pharmacy" title="Inventory"
        subtitle="Medicines, stock, suppliers, movements and purchase history in one place. Stock only changes when a purchase is finalised." />

      <div className="mb-8 flex flex-wrap gap-2">
        {SECTIONS.map(([v, label]) => (
          <button key={v} onClick={() => go(v)} data-testid={`inventory-section-${v}`}
            className={`rounded-full border px-5 py-2 text-sm font-medium ${tab === v
              ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "medicines" && <Medicines embedded />}
      {tab === "stock" && <StockPanel />}
      {tab === "suppliers" && <Suppliers embedded />}
      {tab === "movements" && <MovementsPanel />}
      {tab === "purchases" && <Purchases embedded />}

      <InventoryDangerZone />
    </div>
  );
}
